/**
 * Shared building blocks for the opportunities screens (Google / Material placement): toolbar, list rows, detail sections,
 * facts, activity timeline, dialog actions and the phone FAB. Strings come from the caller; styles are in opportunities.css.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Button, IconChevron, IconSearch } from '@qm/ui/kit';
import { formatDateTime } from '@qm/i18n';
import type { OpportunityView } from '@qm/contracts/opportunities';
import { useOpp } from './env';
import { fmtMoney, pct } from './format';

export const IconPlus = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" /></svg>
);

/** Back link above a detail page. */
export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return <Link to={to} className="qop-back"><IconChevron width={16} height={16} />{children}</Link>;
}

/** Floating action button, phones only (the header button covers wider screens). */
export function Fab({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" className="qop-fab" onClick={onClick}><IconPlus />{label}</button>;
}

/** Gmail / Drive style search pill. */
export function SearchField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="qop-search">
      <IconSearch aria-hidden="true" />
      <span className="qop-sr">{label}</span>
      <input type="search" value={value} placeholder={label} onChange={(e) => onChange(e.target.value)} autoComplete="off" />
    </label>
  );
}

/** A compact chip-style native select (status filter). */
export function ChipSelect({ label, value, onChange, children }: { label: string; value: string; onChange: (v: string) => void; children: ReactNode }) {
  return (
    <label className="qop-chipselect" data-set={value ? '' : undefined}>
      <span className="qop-sr">{label}</span>
      <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>{children}</select>
    </label>
  );
}

/** Underlined view tabs (All / Mine / ...), pressed state announced. */
export function ViewTabs<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: { id: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="qop-tabs" role="group" aria-label={label}>
      {options.map((o) => <button key={o.id} type="button" aria-pressed={value === o.id} onClick={() => onChange(o.id)}>{o.label}</button>)}
    </div>
  );
}

/** A titled section: whitespace, not a box. */
export function Section({ id, title, action, children }: { id: string; title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="qop-sec" aria-labelledby={id}>
      <header className="qop-sec__head"><h2 id={id}>{title}</h2>{action}</header>
      {children}
    </section>
  );
}

export function Facts({ children }: { children: ReactNode }) { return <dl className="qop-facts">{children}</dl>; }
export function Fact({ label, children, dir }: { label: string; children: ReactNode; dir?: 'ltr' }) {
  return <div><dt>{label}</dt><dd dir={dir}>{children}</dd></div>;
}

/** Cancel (text) then the filled primary, bottom-end, inside a dialog form. */
export function DialogActions({ cancelLabel, onCancel, submitLabel, busy, disabled, danger }: { cancelLabel: string; onCancel: () => void; submitLabel: string; busy?: boolean; disabled?: boolean; danger?: boolean }) {
  return (
    <div className="qop-dactions">
      <Button variant="ghost" onClick={onCancel}>{cancelLabel}</Button>
      <Button type="submit" variant={danger ? 'danger' : 'accent'} loading={busy} disabled={disabled}>{submitLabel}</Button>
    </div>
  );
}

/** Activity timeline: dot + line, the event first, actor and time as secondary text. */
export function Activity({ opp, actor }: { opp: OpportunityView; actor?: (e: OpportunityView['events'][number]) => string | null }) {
  const { t, locale } = useOpp();
  return (
    <ol className="qop-activity">
      {[...opp.events].reverse().map((e) => {
        const d = e.detail as Record<string, unknown>;
        const price = [d.requiredPriceMinor, d.offeredTotalMinor, d.totalMinor].find((x) => typeof x === 'number') as number | undefined;
        const who = actor?.(e);
        return (
          <li key={e.id}>
            <span className="qop-activity__what">{t(`opportunities.event.${e.kind}`)}{price !== undefined ? ` · ${fmtMoney(locale, opp.currency, price)}` : ''}{typeof d.discountBps === 'number' && d.discountBps > 0 ? ` (${pct(d.discountBps)})` : ''}</span>
            {typeof d.note === 'string' && d.note ? <span className="qop-activity__note">{d.note}</span> : null}
            {typeof d.reason === 'string' && d.reason ? <span className="qop-activity__note">{d.reason}</span> : null}
            <span className="qop-activity__meta">{who ? <>{who} · </> : null}<time dateTime={e.createdAt}>{formatDateTime(locale, e.createdAt, { style: 'datetime' })}</time></span>
          </li>
        );
      })}
    </ol>
  );
}

/** Centred empty state with one line and the primary action. */
export function Empty({ text, action }: { text: string; action?: ReactNode }) {
  return <div className="qop-empty"><p>{text}</p>{action}</div>;
}
